import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import * as marked from 'marked';

import { AccountService } from '../../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../../services/event-type/event-type.service';
import { EventPricingMatrix, EventQuestion, NonRepeatingEvent } from '../../../../../../../models/event-type';
import {
  PrivateEventBooking,
  PrivateEventBookingReservation
} from '../../../../../../../models/scheduled-event';
import { ScheduledEventService } from '../../../../../../../services/scheduled-event/scheduled-event.service';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import {
  CreateNewCustomerModalComponent
} from '../../../../customers/modals/create-new-customer-modal/create-new-customer-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SelectCustomerModalComponent } from '../../../../modals/select-customer-modal/select-customer-modal.component';
import {
  AddParticipantToPrivateEventModalComponent
} from '../../../../events/modals/add-participant-to-private-event-modal/add-participant-to-private-event-modal.component';
import { SimpleMessageModalComponent } from '../../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import { Customer, CustomerProfileDetails, CustomerSearchResult } from '../../../../../../../models/customer';
import { getCustomerFieldValue } from '../../../../../../../utils-models';
import { CustomerService } from '../../../../../../../services/customer/customer.service';
import { DomSanitizer } from '@angular/platform-browser';
import { ReviewUnlinkedReservationModalComponent } from '../../../../inbox/inbox-events/inbox-unlinked-reservations/modals/review-unlinked-reservation-modal/review-unlinked-reservation-modal.component';
import { InboxService } from '../../../../../../../services/inbox/inbox.service';
import { EventQuestionScope, EventQuestionType } from '../../../../../../../models/event-type-enums';
import { ProfileField } from '../../../../../../../models/organization-config-enums';
import { dateStringFromISO8601, propExistsAndIsNonEmpty, timeStringFromISO8601 } from '../../../../../../../utils';
import { ScheduledBooking } from '../../../../../../../models/scheduled-booking';
import { ScheduledBookingService } from '../../../../../../../services/scheduled-booking/scheduled-booking.service';
import { ComponentHasUnsavedChanges } from '../../../../../../../unsaved-changes-guard.service';


@Component({
  selector: 'app-private-event-edit-reservations',
  templateUrl: './private-event-edit-reservations.component.html',
  styleUrls: ['./private-event-edit-reservations.component.css']
})
export class PrivateEventEditReservationsComponent implements OnInit, ComponentHasUnsavedChanges {

  readonly SUBMIT_TYPE_GO_BACK = 'goBack';
  readonly SUBMIT_TYPE_CONTINUE = 'continue';

  public isNewBooking: boolean = null;
  public privateEvent: NonRepeatingEvent;
  public pricingMatrix: EventPricingMatrix;
  public participantQuestions: EventQuestion[];
  public booking: PrivateEventBooking;
  public requestBooking: ScheduledBooking = null;
  public numReservations: number = null;
  public submitType = this.SUBMIT_TYPE_CONTINUE;
  public showRequestBooking = false;
  public unsavedChanges = false;

  public customer: {[id: number]: Customer} = {};
  public customerProfileDetails: {[id: number]: CustomerProfileDetails} = {};
  public loadingCustomer: {[id: number]: boolean} = {};
  public loadingCustomerProfileDetails: {[id: number]: boolean} = {};
  public customerNotFound: {[id: number]: boolean} = {};

  public errorMessage = '';
  public errors: any = {};
  public successMessage = '';

  public loadingPrivateEvent = true;
  public loadingRequestBooking = false;
  public loadingPricingMatrix = false;
  public loadingEventQuestions = false;
  public loadingBooking = false;
  public loadingUnlinkedReservation = false;
  public loadingSave = false;

  public privateEventNotFound = false;
  public missingPermission = false;
  public serverError: number = null;

  public readonly EventQuestionType = EventQuestionType;
  public readonly timeStringFromISO8601 = timeStringFromISO8601;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly Object = Object;
  public readonly propExistsAndIsNonEmpty = propExistsAndIsNonEmpty;

  constructor(
    public orgConfigService: OrganizationConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private eventTypeService: EventTypeService,
    private scheduledEventService: ScheduledEventService,
    private scheduledBookingService: ScheduledBookingService,
    private customerService: CustomerService,
    private inboxService: InboxService,
    private modalService: NgbModal,
    private domSanitizer: DomSanitizer
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_events) {
      this.missingPermission = true;
      return;
    }

    const routeParams = this.route.snapshot.params;

    this.loadPrivateEvent(routeParams.id);
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  public resetBooking() {
    this.booking = {
      booked_by_id: null,
      booked_by_first_name: null,
      booked_by_last_name: null,
      booked_by_birthday: null,
      booked_by_email: null,
      payment_fee_name: null,
      add_on_names: [],
      per_booking_base_fee: '0.00',
      per_booking_add_on_fee: '0.00',
      total_fee_pre_tax: '0.00',
      total_fee: '0.00',
      discount_amount: '0.00',
      total_fee_without_discount: '0.00',
      amount_due: '0.00',
      amount_paid: '0.00',
      amount_refunded: '0.00',
      invoice_id: null,
      payment_processor_type: null,
      payments: [],
      pending_requested_payment: null,
      reservations: []
    };

    if (this.requestBooking) {
      this.setInitialBookingParametersFromRequestBooking();
    }
  }

  public loadPrivateEvent(eventId: string) {
    this.loadingPrivateEvent = true;

    const eventIdInt = parseInt(eventId, 10);
    if (isNaN(eventIdInt)) {
      this.privateEventNotFound = true;
      this.loadingPrivateEvent = false;
      return;
    }

    this.eventTypeService
      .getNonRepeatingEvent(eventIdInt)
      .then(eventType => {
        this.privateEvent = eventType;

        if (!this.privateEvent.is_private) {
          this.privateEventNotFound = true;
          return;
        }

        this.loadPricingMatrix();
        this.loadEventQuestions();
        this.loadBooking();
        if (this.privateEvent.request_form_booking_id) {
          this.loadRequestBooking();
        }

        // Reset unlinked reservation load indicator, necessary if this was
        // kicked off by a reservation being linked.
        this.loadingUnlinkedReservation = false;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.privateEventNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingPrivateEvent = false; });
  }

  public loadPricingMatrix() {
    this.loadingPricingMatrix = true;

    if (!this.privateEvent.pricing_matrix_id) {
      this.pricingMatrix = null;
      this.loadingPricingMatrix = false;
      return;
    }

    this.eventTypeService
      .getEventPricingMatrix(
        this.privateEvent.event_type_id,
        this.privateEvent.pricing_matrix_id
      )
      .then(matrix => {
        this.pricingMatrix = matrix;

        if (this.booking) {
          this.setInitialBookingFeeName();
          if (this.requestBooking) {
            this.setInitialBookingParametersFromRequestBooking();
          }
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.privateEventNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingPricingMatrix = false; });
  }

  public loadEventQuestions() {
    this.loadingEventQuestions = true;

    this.eventTypeService
      .getEventQuestions(
        this.privateEvent.event_type_id,
        1,
        100
      )
      .then(response => {
        this.participantQuestions = response.results
          .filter(q => q.scope === EventQuestionScope.participant);

        if ((this.participantQuestions.length > 0) && this.booking && this.requestBooking) {
          this.setInitialBookingParametersFromRequestBooking();
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.privateEventNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventQuestions = false; });
  }

  public loadBooking() {
    this.loadingBooking = true;

    this.scheduledEventService
      .getPrivateEventBooking(this.privateEvent.id)
      .then(booking => {
        this.isNewBooking = false;

        this.booking = booking;

        this.numReservations = this.booking.reservations.length;

        for (const reservation of this.booking.reservations) {
          if (reservation.customer_id) {
            this.loadCustomer(reservation.customer_id);
            this.loadCustomerProfileDetails(reservation.customer_id);
          }
        }
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.isNewBooking = true;
          this.resetBooking();

          if (this.pricingMatrix) {
            this.setInitialBookingFeeName();
          }
          if (this.privateEvent.request_form_booking_id) {
            this.showRequestBooking = true;
          }
        } else if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingBooking = false; });
  }

  public loadRequestBooking() {
    if (!this.privateEvent.request_form_booking_id) { return; }

    this.loadingRequestBooking = true;

    this.scheduledBookingService
      .getScheduledBooking(this.privateEvent.request_form_booking_id)
      .then(scheduledBooking => {
        this.requestBooking = scheduledBooking;
        if (this.isNewBooking) {
          this.setInitialBookingParametersFromRequestBooking();
          this.showRequestBooking = true;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.privateEventNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingRequestBooking = false; });
  }

  public save() {
    this.persistBooking().then(saved => {
      if (!saved) { return; }

      // Routing depends on which save button was clicked.
      if (this.submitType === this.SUBMIT_TYPE_GO_BACK) {
        this.router.navigate(
          [this.getBackLink()],
          {
            state: {
              feedback: {
                text: this.privateEvent.event_type_name + ' private event saved successfully.',
                status: 'success'
              }
            }
          }
        );
      } else if (this.submitType === this.SUBMIT_TYPE_CONTINUE) {
        this.router.navigate(
          ['/events/scheduled/private-event/edit/' + this.privateEvent.id.toString() + '/payment']
        );
      }
    });
  }

  /**
   * Validate and persist the booking. Resolves to true on success, or false when
   * validation failed or the save errored (errors are surfaced via this.errors /
   * this.errorMessage / this.serverError). Shared by save() and the "Add
   * Participant" flow, which must persist the booking before adding a participant.
   */
  private persistBooking(): Promise<boolean> {
    this.loadingSave = true;

    this.errorMessage = '';
    this.errors = {};
    this.successMessage = '';

    if ((this.numReservations === null) || (this.numReservations === undefined)) {
      this.errors = {
        numReservations: 'This field is required.'
      };
      this.errorMessage = 'Please correct the errors above and try again.';
      this.loadingSave = false;
      return Promise.resolve(false);
    }

    if (this.numReservations <= 0) {
      this.errors = {
        numReservations: 'This value must be at least 1.'
      };
      this.errorMessage = 'Please correct the errors above and try again.';
      this.loadingSave = false;
      return Promise.resolve(false);
    }

    if (this.numReservations > 1000) {
      this.errors = {
        numReservations: 'This value must be at most 1000.'
      };
      this.errorMessage = 'Please correct the errors above and try again.';
      this.loadingSave = false;
      return Promise.resolve(false);
    }

    this.booking.reservations = this.booking.reservations.slice(0, this.numReservations);

    return this.scheduledEventService
      .setPrivateEventBooking(this.privateEvent.id, this.booking)
      .then(updatedBooking => {
        this.booking = updatedBooking;

        // Changes have been persisted, so clear the unsaved-changes flag before
        // navigating away (otherwise the route guard would warn on this save).
        this.unsavedChanges = false;

        return true;
      }).catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to modify this private event.';
        } else {
          this.serverError = reason.status;
        }

        return false;
      })
      .finally(() => { this.loadingSave = false; });
  }

  /**
   * PRO-1221: Save the booking, then open the "Add Participant" modal. The
   * booking must be persisted first so the modal operates on (and the backend
   * sees) the current reservation set. No event part id is provided here, so no
   * immediate check-in is offered.
   */
  public openAddParticipant() {
    this.persistBooking().then(saved => {
      if (!saved) { return; }

      const modalRef = this.modalService.open(AddParticipantToPrivateEventModalComponent);
      modalRef.componentInstance.privateEventId = this.privateEvent.id;
      modalRef.componentInstance.booking = this.booking;
      modalRef.componentInstance.eventPartId = null;
      modalRef.componentInstance.participantAddedCallback = () => {
        // Reload the booking so the new participant appears and numReservations
        // is updated to the new count.
        this.loadBooking();
      };
    });
  }

  /**
   * Get the link back to the private event's details view.
   */
  public getBackLink(): string {
    if (this.privateEvent && (this.privateEvent.event_parts.length > 0)) {
      return '/events/scheduled/now/event/' + this.privateEvent.event_parts[0].id.toString();
    } else {
      return '/events/scheduled/now';
    }
  }

  public handleNumReservationsChange() {
    this.unsavedChanges = true;

    if ((this.numReservations === null) || (this.numReservations === undefined)) {
      return;
    }

    if (this.numReservations <= 0) {
      return;
    }

    if (this.numReservations > 1000) {
      return;
    }

    while (this.numReservations > this.booking.reservations.length) {
      this.addReservation();
    }
  }

  public toggleAddOn(addOnNames: string[], addOnName: string) {
    this.unsavedChanges = true;

    if (addOnNames.includes(addOnName)) {
      while (addOnNames.includes(addOnName)) {
        addOnNames.splice(addOnNames.indexOf(addOnName), 1);
      }
    } else {
      addOnNames.push(addOnName);
    }
  }

  public openSelectBookingCustomer() {
    const modalRef = this.modalService.open(SelectCustomerModalComponent);
    modalRef.componentInstance.excludeCustomerIds = [];
    modalRef.componentInstance.submitCallback = (result: CustomerSearchResult) => {
      this.booking.booked_by_id = result.id;
      this.booking.booked_by_first_name = result.first_name;
      this.booking.booked_by_last_name = result.last_name;
      this.booking.booked_by_birthday = result.birthday;
      this.booking.booked_by_email = result.email;

      this.unsavedChanges = true;
    };
  }

  public openCreateNewBookingCustomer() {
    const modalRef = this.modalService.open(CreateNewCustomerModalComponent);
    modalRef.componentInstance.savedCallback = (result: Customer) => {
      this.booking.booked_by_id = result.id;
      this.booking.booked_by_first_name = result.first_name;
      this.booking.booked_by_last_name = result.last_name;
      this.booking.booked_by_birthday = result.birthday;
      this.booking.booked_by_email = result.email;

      this.unsavedChanges = true;
    };
  }

  public openSelectReservationCustomer(index: number) {
    const modalRef = this.modalService.open(SelectCustomerModalComponent);
    modalRef.componentInstance.excludeCustomerIds = [];
    modalRef.componentInstance.submitCallback = (result: CustomerSearchResult) => {
      // Reject the selection if this customer is already a participant on a
      // different reservation, rather than silently filtering them out of the
      // search results (which staff found confusing on large events).
      const alreadyParticipant = this.booking.reservations.some(
        (res, resIndex) => (resIndex !== index) && (res.customer_id === result.id)
      );

      if (alreadyParticipant) {
        const errorModalRef = this.modalService.open(SimpleMessageModalComponent);
        errorModalRef.componentInstance.headerText = 'Customer Already Added';
        errorModalRef.componentInstance.messageText = (
          result.first_name + ' ' + result.last_name +
          ' is already a participant on this private event.'
        );
        errorModalRef.componentInstance.messageAlertClass = 'alert-danger';
        return;
      }

      this.booking.reservations[index].customer_id = result.id;
      this.booking.reservations[index].first_name = result.first_name;
      this.booking.reservations[index].last_name = result.last_name;
      this.booking.reservations[index].birthday = result.birthday;
      this.booking.reservations[index].email = result.email;

      this.unsavedChanges = true;

      this.loadCustomer(result.id);
      this.loadCustomerProfileDetails(result.id);
    };
  }

  public openCreateNewReservationCustomer(index: number) {
    const modalRef = this.modalService.open(CreateNewCustomerModalComponent);
    modalRef.componentInstance.savedCallback = (result: Customer) => {
      this.booking.reservations[index].customer_id = result.id;
      this.booking.reservations[index].first_name = result.first_name;
      this.booking.reservations[index].last_name = result.last_name;
      this.booking.reservations[index].birthday = result.birthday;
      this.booking.reservations[index].email = result.email;

      this.unsavedChanges = true;

      this.loadCustomer(result.id);
      this.loadCustomerProfileDetails(result.id);
    };
  }

  public clearReservationCustomer(reservation: PrivateEventBookingReservation) {
    reservation.first_name = '';
    reservation.last_name = '';
    reservation.birthday = '';
    reservation.email = '';
    reservation.customer_id = null;

    this.unsavedChanges = true;
  }

  public getCustomerGymStatus(customerId: number) {
    if (!this.customer[customerId]) { return 'Loading...'; }

    return getCustomerFieldValue(
      ProfileField.gym_status,
      this.customer[customerId],
      this.customerProfileDetails[customerId],
      this.loadingCustomer[customerId] || this.loadingCustomerProfileDetails[customerId]
    );
  }

  public getRenderedInlineMarkdown(content: string) {
    return this.domSanitizer.bypassSecurityTrustHtml(
      marked.parseInline(
        content,
        {
          breaks: true
        }
      )
    );
  }

  public singleCheckboxChecked(questionResponses: {[key: string]: any}, questionName: string): boolean {
    return questionResponses[questionName] === 'on';
  }

  public toggleSingleCheckbox(questionResponses: {[key: string]: any}, questionName: string) {
    this.unsavedChanges = true;

    if (this.singleCheckboxChecked(questionResponses, questionName)) {
      questionResponses[questionName] = null;
    } else {
      questionResponses[questionName] = 'on';
    }
  }

  public multiCheckboxChecked(questionResponses: {[key: string]: any}, questionName: string, option: string): boolean {
    const questionResponse = questionResponses[questionName];

    if (!questionResponse) {
      return false;
    }

    return questionResponse.includes(option);
  }

  public toggleMultiCheckbox(questionResponses: {[key: string]: any}, questionName: string, option: string) {
    this.unsavedChanges = true;

    if (!questionResponses[questionName]) {
      questionResponses[questionName] = [option];
      return;
    }

    const questionResponse: string[] = questionResponses[questionName];

    if (questionResponse.includes(option)) {
      questionResponse.splice(questionResponse.indexOf(option), 1);
    } else {
      questionResponse.push(option);
    }
  }

  public openReviewUnlinkedReservationModal(reservation: PrivateEventBookingReservation) {
    this.successMessage = '';

    this.loadingUnlinkedReservation = true;

    this.inboxService
      .getUnlinkedReservation(reservation.id)
      .then(unlinkedReservation => {
        const modalRef = this.modalService.open(ReviewUnlinkedReservationModalComponent);
        modalRef.componentInstance.unlinkedReservation = unlinkedReservation;
        modalRef.componentInstance.showHideOption = false;
        modalRef.componentInstance.savedCallback = () => {
          // Ensure the account link updates.
          this.loadPrivateEvent(this.privateEvent.id.toString());

          // Set the loading state back to true - will be reset when the event reloads.
          this.loadingUnlinkedReservation = true;
        };
      })
      .catch(reason => {
        if (reason.status === 404) {
          // 404 indicates that the reservation is no longer unlinked, so reload.
          this.loadPrivateEvent(this.privateEvent.id.toString());
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingUnlinkedReservation = false;
      });
  }

  private addReservation() {
    const newReservation: PrivateEventBookingReservation = {
      first_name: '',
      last_name: '',
      birthday: '',
      email: '',
      customer_id: null,
      question_responses: {},
      payment_fee_name: null,
      add_on_names: [],
      per_participant_base_fee: '0.00',
      per_participant_add_on_fee: '0.00',
      confirmation_email_sent: false,
      send_confirmation_email: false
    };

    if (this.pricingMatrix && (this.pricingMatrix.per_participant_fees.length > 0)) {
      newReservation.payment_fee_name = this.pricingMatrix.per_participant_fees[0].name;
    }

    this.booking.reservations.push(newReservation);
  }

  private setInitialBookingFeeName() {
    if (this.pricingMatrix && (this.pricingMatrix.per_booking_fees.length > 0) && this.isNewBooking) {
      this.booking.payment_fee_name = this.pricingMatrix.per_booking_fees[0].name;
    }
  }

  /**
   * Load the customer with the given ID.
   * @param customerIdInt - The API ID of the customer.
   */
  private loadCustomer(customerIdInt: number) {
    this.loadingCustomer[customerIdInt] = true;

    this.customerService
      .getCustomer(customerIdInt)
      .then(customer => {
        this.customer[customerIdInt] = customer;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound[customerIdInt] = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCustomer[customerIdInt] = false; });
  }

  /**
   * Load the profile details of the customer with the given ID.
   * @param customerIdInt - The API ID of the customer.
   */
  private loadCustomerProfileDetails(customerIdInt: number) {
    this.loadingCustomerProfileDetails[customerIdInt] = true;

    this.customerNotFound[customerIdInt] = false;

    this.customerService
      .getCustomerProfileDetails(customerIdInt)
      .then(customerProfileDetails => {
        this.customerProfileDetails[customerIdInt] = customerProfileDetails;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.customerNotFound[customerIdInt] = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingCustomerProfileDetails[customerIdInt] = false;
      });
  }

  private setInitialBookingParametersFromRequestBooking() {
    if (!this.requestBooking) { return; }

    if (this.requestBooking.booked_by_customer_id) {
      this.booking.booked_by_id = this.requestBooking.booked_by_customer_id;
      this.booking.booked_by_first_name = this.requestBooking.booked_by_customer_first_name;
      this.booking.booked_by_last_name = this.requestBooking.booked_by_customer_last_name;
      this.booking.booked_by_birthday = this.requestBooking.booked_by_customer_birthday;
      this.booking.booked_by_email = this.requestBooking.booked_by_customer_email;
    }

    let paymentFeeName: string= null;
    if (this.pricingMatrix && (this.pricingMatrix.per_participant_fees.length > 0)) {
      paymentFeeName = this.pricingMatrix.per_participant_fees[0].name;
    }

    this.numReservations = this.requestBooking.active_reservations.length;
    this.booking.reservations = [];
    for (const res of this.requestBooking.active_reservations) {
      const questionResponses: any = {};

      if (this.participantQuestions) {
        for (const question of this.participantQuestions) {
          if (res.question_responses.hasOwnProperty(question.name)) {
            questionResponses[question.name] = res.question_responses[question.name];
          }
        }
      }

      this.booking.reservations.push({
        customer_id: res.customer_id,
        first_name: res.first_name,
        last_name: res.last_name,
        birthday: res.birthday,
        preferred_name: res.preferred_name,
        email: res.email,
        question_responses: questionResponses,
        payment_fee_name: paymentFeeName,
        add_on_names: [],
        per_participant_base_fee: '0.00',
        per_participant_add_on_fee: '0.00',
        confirmation_email_sent: false,
        send_confirmation_email: false
      });
      if (res.customer_id) {
        this.loadCustomer(res.customer_id);
        this.loadCustomerProfileDetails(res.customer_id);
      }
    }
  }
}
